import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';

const ApiKeys = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [apiKeys, setApiKeys] = useState([
    {
      id: '1',
      name: 'Production API Key',
      key: 'lp_prod_sk_1234567890abcdef',
      type: 'production',
      createdAt: '2024-01-15',
      lastUsed: '2 hours ago',
      permissions: ['leads:read', 'leads:write', 'campaigns:read'],
      isActive: true
    },
    {
      id: '2',
      name: 'Development API Key',
      key: 'lp_dev_sk_abcdef1234567890',
      type: 'development',
      createdAt: '2024-01-10',
      lastUsed: '1 day ago',
      permissions: ['leads:read', 'campaigns:read'],
      isActive: true
    },
    {
      id: '3',
      name: 'Webhook API Key',
      key: 'lp_webhook_sk_9876543210fedcba',
      type: 'webhook',
      createdAt: '2024-01-05',
      lastUsed: 'Never',
      permissions: ['webhooks:write'],
      isActive: false
    }
  ]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  const [revealedKeys, setRevealedKeys] = useState(new Set());
  const [newKeyForm, setNewKeyForm] = useState({
    name: '',
    type: 'development',
    permissions: []
  });

  // Fallback icon component
  const IconComponent = ({ name, size = 16, className = "" }) => {
    const iconMap = {
      'ArrowLeft': '←',
      'Plus': '+',
      'Eye': '👁',
      'EyeOff': '🙈',
      'Copy': '📋',
      'Trash2': '🗑',
      'BookOpen': '📖',
      'Code': '💻',
      'Webhook': '🔗',
      'MessageSquare': '💬',
      'X': '✗',
      'AlertTriangle': '⚠️'
    };
    
    return (
      <span className={`inline-block ${className}`} style={{ fontSize: size }}>
        {iconMap?.[name] || '⚫'}
      </span>
    );
  };

  const availablePermissions = [
    { id: 'leads:read', label: 'Read Leads', description: 'View lead data and lists' },
    { id: 'leads:write', label: 'Write Leads', description: 'Create and update leads' },
    { id: 'campaigns:read', label: 'Read Campaigns', description: 'View campaign data' },
    { id: 'campaigns:write', label: 'Write Campaigns', description: 'Create and manage campaigns' },
    { id: 'analytics:read', label: 'Read Analytics', description: 'Access analytics data' },
    { id: 'webhooks:write', label: 'Webhook Access', description: 'Receive webhook notifications' }
  ];

  const handleCreateApiKey = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newKey = {
        id: Date.now()?.toString(),
        name: newKeyForm?.name,
        key: `lp_${newKeyForm?.type}_sk_${Math.random()?.toString(36)?.substr(2, 16)}`,
        type: newKeyForm?.type,
        createdAt: new Date()?.toISOString()?.split('T')?.[0],
        lastUsed: 'Never',
        permissions: newKeyForm?.permissions,
        isActive: true
      };

      setApiKeys(prev => [...prev, newKey]);
      setSelectedKey(newKey);
      setShowCreateModal(false);
      setShowKeyModal(true);
      setNewKeyForm({ name: '', type: 'development', permissions: [] });
    } catch (error) {
      console.error('Error creating API key:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApiKey = (keyId) => {
    if (confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      setApiKeys(prev => prev?.filter(key => key?.id !== keyId));
    }
  };

  const handleToggleKey = (keyId) => {
    setApiKeys(prev => prev?.map(key => 
      key?.id === keyId ? { ...key, isActive: !key?.isActive } : key
    ));
  };

  const handleRevealKey = (keyId) => {
    setRevealedKeys(prev => {
      const newSet = new Set(prev);
      if (newSet?.has(keyId)) {
        newSet?.delete(keyId);
      } else {
        newSet?.add(keyId);
      }
      return newSet;
    });
  };

  const maskApiKey = (key) => {
    const parts = key?.split('_');
    if (parts?.length >= 3) {
      return `${parts?.[0]}_${parts?.[1]}_${'•'?.repeat(16)}`;
    }
    return '•'?.repeat(key?.length - 8) + key?.slice(-8);
  };

  const getKeyTypeColor = (type) => {
    switch (type) {
      case 'production':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'development':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'webhook':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    // You could add a toast notification here
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>API Keys - LeadGen Pro</title>
        <meta name="description" content="Manage your API keys and integrations" />
      </Helmet>
      <Header />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-2">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate(-1)}
                  className="flex items-center space-x-1"
                >
                  <IconComponent name="ArrowLeft" />
                </Button>
                <h1 className="text-3xl font-bold text-gray-900">API Keys</h1>
              </div>
              <p className="text-gray-600">
                Manage your API keys and access tokens for integrations
              </p>
            </div>
            
            <Button 
              variant="default" 
              onClick={() => setShowCreateModal(true)}
              className="flex items-center space-x-2"
            >
              <IconComponent name="Plus" />
              <span>Create API Key</span>
            </Button>
          </div>

          {/* API Keys List */}
          <div className="space-y-6">
            {apiKeys?.map((apiKey) => (
              <div key={apiKey?.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      <h3 className="text-lg font-semibold text-gray-900">{apiKey?.name}</h3>
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full border ${getKeyTypeColor(apiKey?.type)}`}>
                        {apiKey?.type?.charAt(0)?.toUpperCase() + apiKey?.type?.slice(1)}
                      </span>
                      <div className={`w-2 h-2 rounded-full ${apiKey?.isActive ? 'bg-green-500' : 'bg-gray-400'}`} />
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <code className="text-sm font-mono text-gray-900">
                            {revealedKeys?.has(apiKey?.id) ? apiKey?.key : maskApiKey(apiKey?.key)}
                          </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRevealKey(apiKey?.id)}
                            className="flex items-center"
                          >
                            <IconComponent name={revealedKeys?.has(apiKey?.id) ? "EyeOff" : "Eye"} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => copyToClipboard(apiKey?.key)}
                            className="flex items-center"
                          >
                            <IconComponent name="Copy" />
                          </Button>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-gray-600">Created</p>
                        <p className="text-sm font-medium text-gray-900">{new Date(apiKey.createdAt)?.toLocaleDateString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Last Used</p>
                        <p className="text-sm font-medium text-gray-900">{apiKey?.lastUsed}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-600">Permissions</p>
                        <p className="text-sm font-medium text-gray-900">{apiKey?.permissions?.length} permissions</p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <p className="text-xs text-gray-600 mb-2">Permissions</p>
                      <div className="flex flex-wrap gap-2">
                        {apiKey?.permissions?.map((permission) => (
                          <span key={permission} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                            {permission}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleToggleKey(apiKey?.id)}
                      className={apiKey?.isActive ? 'text-orange-600' : 'text-green-600'}
                    >
                      {apiKey?.isActive ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteApiKey(apiKey?.id)}
                      className="text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <IconComponent name="Trash2" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* API Documentation */}
          <div className="mt-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">API Documentation</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <IconComponent name="BookOpen" size={24} className="text-blue-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Quick Start Guide</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Learn how to get started with our API in just a few minutes.
                    </p>
                    <Button variant="outline" size="sm">View Guide</Button>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <IconComponent name="Code" size={24} className="text-blue-500 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">API Reference</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Comprehensive documentation for all API endpoints.
                    </p>
                    <Button variant="outline" size="sm">View Reference</Button>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <IconComponent name="Webhook" size={24} className="text-purple-500 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Webhooks</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Set up webhooks to receive real-time notifications.
                    </p>
                    <Button variant="outline" size="sm">Setup Webhooks</Button>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <IconComponent name="MessageSquare" size={24} className="text-green-500 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Support</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Need help? Our support team is here to assist you.
                    </p>
                    <Button variant="outline" size="sm">Contact Support</Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white border border-gray-200 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">Create API Key</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateModal(false)}
                className="flex items-center"
              >
                <IconComponent name="X" />
              </Button>
            </div>

            <div className="space-y-4">
              <Input
                label="API Key Name"
                type="text"
                value={newKeyForm?.name}
                onChange={(e) => setNewKeyForm(prev => ({ ...prev, name: e?.target?.value }))}
                placeholder="e.g., My Integration"
                required
              />

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">Type</label>
                <select
                  value={newKeyForm?.type}
                  onChange={(e) => setNewKeyForm(prev => ({ ...prev, type: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="development">Development</option>
                  <option value="production">Production</option>
                  <option value="webhook">Webhook</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">Permissions</label>
                <div className="space-y-3 max-h-48 overflow-y-auto">
                  {availablePermissions?.map((permission) => (
                    <label key={permission?.id} className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newKeyForm?.permissions?.includes(permission?.id)}
                        onChange={(e) => {
                          if (e?.target?.checked) {
                            setNewKeyForm(prev => ({
                              ...prev,
                              permissions: [...prev?.permissions, permission?.id]
                            }));
                          } else {
                            setNewKeyForm(prev => ({
                              ...prev,
                              permissions: prev?.permissions?.filter(p => p !== permission?.id)
                            }));
                          }
                        }}
                        className="mt-1"
                      />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{permission?.label}</p>
                        <p className="text-xs text-gray-600">{permission?.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 mt-6">
              <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleCreateApiKey}
                disabled={loading || !newKeyForm?.name || newKeyForm?.permissions?.length === 0}
                className="flex items-center space-x-2"
              >
                {loading && <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />}
                <span>Create Key</span>
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Show New Key Modal */}
      {showKeyModal && selectedKey && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white border border-gray-200 rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900">API Key Created</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowKeyModal(false)}
                className="flex items-center"
              >
                <IconComponent name="X" />
              </Button>
            </div>

            <div className="mb-6">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <IconComponent name="AlertTriangle" size={16} className="text-yellow-600 mt-0.5" />
                  <p className="text-sm text-yellow-800">
                    <strong>Important:</strong> This is the only time we'll show your API key. 
                    Please copy it and store it securely.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <code className="text-sm font-mono text-gray-900 break-all">
                    {selectedKey?.key}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(selectedKey?.key)}
                    className="flex items-center ml-2"
                  >
                    <IconComponent name="Copy" />
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end">
              <Button variant="default" onClick={() => setShowKeyModal(false)}>
                I've Copied My Key
              </Button>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default ApiKeys;